import { cn } from '@/utils/tailwind';

export interface ProgressSegment {
  color: string;
  percentage: number;
}

/**
 * CircularProgress renders a circular progress bar with multiple segments.
 * Each segment can have its own color and percentage of the circle.
 *
 * @example
 * ```tsx
 * <CircularProgress
 *   segments={[
 *     { color: '#ff0000', percentage: 0.3 }, // 30% red
 *     { color: '#00ff00', percentage: 0.5 }, // 50% green
 *     { color: '#0000ff', percentage: 0.1 }, // 10% blue
 *   ]}
 *   size={120}
 *   strokeWidth={8}
 * />
 * ```
 */
export interface CircularProgressProps {
  segments: ProgressSegment[];
  size?: number;
  strokeWidth?: number;
  className?: string;
  testId?: string;
}

export function CircularProgress({
  segments,
  size = 100,
  strokeWidth = 10,
  className,
  testId,
}: CircularProgressProps) {
  // Calculate dimensions
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  // Calculate segment paths
  let currentAngle = 0;

  return (
    <div
      className={cn('relative inline-block', className)}
      data-testid={testId}
      style={{ width: size, height: size }}
    >
      {/* Background circle */}
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="var(--gray-500)"
          strokeWidth={strokeWidth}
          className="opacity-20"
        />

        {/* Segments */}
        {segments.map((segment, index) => {
          const strokeDasharray = circumference;
          const normalizedPercentage = Math.min(segment.percentage, 1);
          const strokeDashoffset = circumference * (1 - normalizedPercentage);

          // Calculate rotation for this segment
          const rotation = currentAngle * 360 - 90; // -90 to start at top
          currentAngle += normalizedPercentage;

          return (
            <circle
              key={index}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              style={{
                transform: `rotate(${rotation}deg)`,
                transformOrigin: `${center}px ${center}px`,
                transition: 'all 0.5s ease-in-out',
              }}
            />
          );
        })}
      </svg>
    </div>
  );
}
